import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { basisLabel, fmt, pct, downloadCSV } from "@/lib/utils-msip";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, Download, Pin, X, TrendingUp, TrendingDown, Minus } from "lucide-react";

const QUAD = {
  fortress: { label: "Fortress", color: "#15803d", hint: "High TIV, ≥ target — defend" },
  battleground: { label: "Battleground", color: "#BB162B", hint: "High TIV, < target — FOCUS" },
  niche: { label: "Niche Wins", color: "#0369a1", hint: "Low TIV, ≥ target — maintain" },
  longtail: { label: "Long Tail", color: "#94a3b8", hint: "Low TIV, < target — monitor" },
};

const Trend = ({ t }) => (t === "up" ? <TrendingUp size={14} className="text-emerald-600" /> : t === "down" ? <TrendingDown size={14} className="text-red-600" /> : <Minus size={14} className="text-muted-foreground" />);

export default function FocusMarket() {
  const { filters } = useApp();
  const [data, setData] = useState(null);
  const [pinRto, setPinRto] = useState("");
  const [pinNote, setPinNote] = useState("");

  const load = useCallback(() => {
    api
      .get(`/focus?${qs({ brand: filters.brand, basis: filters.basis, period: "ytd", district: filters.district, territory_only: filters.territoryOnly })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [filters]);

  useEffect(load, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading…</div>;

  const setPin = async (rto, action, note) => {
    try {
      await api.post("/admin/pins", { rto_code: rto, brand: filters.brand, action, note: note || null });
      toast.success(action === "clear" ? `Cleared override for ${rto}` : `${action === "pin" ? "Pinned" : "Excluded"} ${rto}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportCsv = () =>
    downloadCSV(
      `focus_list_${filters.brand}.csv`,
      ["RTO", "Share %", "Trend", "Rival %", "Unit Gap", "TIV", "Pinned"],
      data.focus_list.map((p) => [p.rto, p.share, p.trend, p.rival_share, p.unit_gap, p.tiv, p.pinned ? "yes" : ""]),
      filters.basis
    );

  const counts = data.points.reduce((acc, p) => ({ ...acc, [p.quadrant]: (acc[p.quadrant] || 0) + 1 }), {});

  return (
    <div className="space-y-5" data-testid="focus-market">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Focus Market Engine</h1>
        <span className="text-xs text-muted-foreground" data-testid="focus-period-label"><b className="text-foreground">YTD basis</b> · {data.period?.label} · {basisLabel(filters.basis)} · Target {pct(data.target)}</span>
        <div className="ml-auto flex gap-2 print-hidden">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="focus-export-csv-btn"><Download size={14} className="mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="focus-print-btn"><Printer size={14} className="mr-1" /> Print</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(QUAD).map(([k, q]) => (
          <div key={k} className="border bg-card rounded-md p-3" data-testid={`quad-card-${k}`}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: q.color }} />
              <span className="text-sm font-semibold">{q.label}</span>
              <span className="num ml-auto font-bold">{counts[k] || 0}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{q.hint}</p>
          </div>
        ))}
      </div>

      <div className="border bg-card rounded-md p-4" data-testid="quadrant-chart">
        <h3 className="font-heading text-sm font-semibold mb-2">TIV vs {filters.brand} share — bubble size = unit gap</h3>
        <ResponsiveContainer width="100%" height={380}>
          <ScatterChart margin={{ top: 10, right: 20, left: -8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
            <XAxis type="number" dataKey="tiv" name="TIV" tick={{ fontSize: 11 }} />
            <YAxis type="number" dataKey="share" name="Share" unit="%" tick={{ fontSize: 11 }} />
            <ZAxis type="number" dataKey="unit_gap" range={[40, 420]} name="Unit gap" />
            <ReferenceLine x={data.median_tiv} stroke="#64748b" strokeDasharray="4 4" label={{ value: "Median TIV", fontSize: 10, position: "insideTopRight" }} />
            <ReferenceLine y={data.target} stroke="#BB162B" strokeDasharray="4 4" label={{ value: `Target ${data.target}%`, fontSize: 10, position: "insideTopRight" }} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
              const p = payload?.[0]?.payload;
              if (!p) return null;
              return (
                <div className="bg-card border rounded-md p-2 text-xs shadow-sm">
                  <b>{p.rto}</b> · {QUAD[p.quadrant].label}<br />TIV {fmt(p.tiv)} · Share {pct(p.share)}<br />Unit gap {fmt(p.unit_gap)} · Rival {pct(p.rival_share)}
                </div>
              );
            }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {Object.entries(QUAD).map(([k, q]) => (
              <Scatter key={k} name={q.label} data={data.points.filter((p) => p.quadrant === k)} fill={q.color} fillOpacity={0.7} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="border bg-card rounded-md" data-testid="focus-list">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b">
          <h3 className="font-heading text-sm font-semibold">Focus List — where to deploy effort ({data.focus_list.length} RTOs)</h3>
          <div className="ml-auto flex gap-2 print-hidden">
            <Select value={pinRto || undefined} onValueChange={setPinRto}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="pin-rto-select"><SelectValue placeholder="Pin an RTO…" /></SelectTrigger>
              <SelectContent>
                {data.points.filter((p) => p.quadrant !== "battleground" && !p.pinned).map((p) => (
                  <SelectItem key={p.rto} value={p.rto}>{p.rto}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="note" value={pinNote} onChange={(e) => setPinNote(e.target.value)} className="h-8 w-32 text-xs" data-testid="pin-note-input" />
            <Button size="sm" variant="outline" disabled={!pinRto} data-testid="pin-add-btn" onClick={() => { setPin(pinRto, "pin", pinNote); setPinRto(""); setPinNote(""); }}>
              <Pin size={13} className="mr-1" /> Pin
            </Button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b">
            <tr>
              {["#", "RTO", "Share %", "Trend", "Rival %", "Unit Gap", "TIV", ""].map((h, i) => (
                <th key={i} className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground ${i >= 2 && i <= 6 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.focus_list.map((p, i) => (
              <tr key={p.rto} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-3 py-1.5 num text-muted-foreground">{i + 1}</td>
                <td className="px-3 py-1.5 font-medium">
                  <Link to={`/rto/${encodeURIComponent(p.rto)}`} className="hover:underline">{p.rto}</Link>
                  {p.pinned && <span className="ml-1.5 text-[10px] rounded bg-amber-50 text-amber-800 px-1 py-0.5">PINNED{p.pin_note ? `: ${p.pin_note}` : ""}</span>}
                </td>
                <td className="px-3 py-1.5 text-right num">{pct(p.share)}</td>
                <td className="px-3 py-1.5 text-right"><span className="inline-flex justify-end"><Trend t={p.trend} /></span></td>
                <td className="px-3 py-1.5 text-right num text-muted-foreground">{pct(p.rival_share)}</td>
                <td className="px-3 py-1.5 text-right num font-bold">{fmt(p.unit_gap)}</td>
                <td className="px-3 py-1.5 text-right num">{fmt(p.tiv)}</td>
                <td className="px-3 py-1.5 text-right print-hidden">
                  <button className="text-muted-foreground hover:text-red-600" title={p.pinned ? "Unpin" : "Exclude from list"} data-testid={`focus-remove-${p.rto}`}
                    onClick={() => setPin(p.rto, p.pinned ? "clear" : "exclude", null)}>
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
